import { useCallback, useEffect, useId, useState } from 'react';
import { useI18n } from '../i18n';
import type { Strings } from '../i18n';
import { useAnnounce } from '../ui/a11y';
import { Dialog } from '../ui/Dialog';
import { errorText, fmtDate, rpc } from './admin/api';

type RoleName = 'school_admin' | 'head_of_school' | 'counsellor' | 'teacher';

type Request = {
  id: string;
  role: RoleName;
  /** Counsellor scope asked for: null = whole school, otherwise grade labels. */
  scope_grades: string[] | null;
  make_primary: boolean;
  subject_name: string;
  subject_is_test: boolean;
  subject_roles: { role: RoleName; is_primary: boolean }[];
  requested_by_name: string;
  requested_by_me: boolean;
  cannot_decide: 'own_request' | null;
  created_at: string;
  expires_at: string;
};

// The Approvals card on the head's home (spec E3, H2, H5): roles that widen
// what someone can see wait here for the primary head of school — and for
// the approval delegate, when one is named. Each request shows what the
// person can do now and what they could do if approved (Before / After), who
// asked, and how long is left before it expires. A delegate never decides a
// request they made or one about themselves; the database enforces that too.
export function HeadApprovals({ asDelegate, onChanged }: { asDelegate: boolean; onChanged?: () => void }) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [rows, setRows] = useState<Request[] | null>(null);
  const [decide, setDecide] = useState<{ r: Request; approve: boolean } | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await rpc<Request[]>('school_list_approvals');
    if (r.error) setLoadError(errorText(t, r.error));
    else {
      setLoadError(null);
      setRows(r.data);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const roleName = (r: RoleName) => t[`pa.roleName_${r}`];

  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>
        {t['head.approvals'](rows?.length ?? 0)}
      </h2>
      <p className="form-hint">{asDelegate ? t['hs.approvalsHintDelegate'] : t['head.approvalsHint']}</p>
      {loadError ? (
        <p className="error-text" role="alert">
          {loadError}
        </p>
      ) : !rows ? (
        <p role="status" className="text-muted">
          {t.loading}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-muted">{t['hs.approvalsNone']}</p>
      ) : (
        <ul className="approval-list">
          {rows.map((r) => (
            <li key={r.id} className="approval">
              <p className="approval-what">
                {t['pa.apWiden'](r.subject_name, roleName(r.role))}
                {r.subject_is_test && (
                  <>
                    {' '}
                    <span className="test-badge">{t['ctx.testBadge']}</span>
                  </>
                )}
              </p>
              <dl className="kv kv-inline">
                <div>
                  <dt>{t['hs.before']}</dt>
                  <dd>{beforeText(t, r)}</dd>
                </div>
                <div>
                  <dt>{t['hs.after']}</dt>
                  <dd>{afterText(t, r)}</dd>
                </div>
              </dl>
              <p className="text-muted">
                {t['pa.requestedBy'](r.requested_by_name, fmtDate(locale, r.created_at))} · {t['pa.expiresOn'](fmtDate(locale, r.expires_at))} ·{' '}
                {t['hs.daysLeft'](daysLeft(r.expires_at))}
              </p>
              {r.cannot_decide ? (
                <p className="notice">{asDelegate ? t['hs.cannotOwn'] : t['hs.cannotOwnPrimary']}</p>
              ) : (
                <div className="form-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setDecide({ r, approve: false })}>
                    {t['pa.reject']}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={() => setDecide({ r, approve: true })}>
                    {t['pa.approve']}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <Dialog
        open={!!decide}
        title={decide?.approve ? t['pa.approveTitle'] : t['pa.rejectTitle']}
        intro={
          decide && (
            <>
              <p className="form-hint">{t['pa.apWiden'](decide.r.subject_name, roleName(decide.r.role))}</p>
              <p className="form-hint">
                {t['hs.before']}: {beforeText(t, decide.r)} → {t['hs.after']}: {afterText(t, decide.r)}
              </p>
            </>
          )
        }
        submitLabel={decide?.approve ? t['pa.approve'] : t['pa.reject']}
        busy={busy}
        error={error}
        onClose={() => {
          setDecide(null);
          setError(null);
        }}
        onSubmit={async () => {
          if (!decide) return;
          setBusy(true);
          setError(null);
          const r = await rpc('school_decide_approval', { p_approval_id: decide.r.id, p_approve: decide.approve, p_note: note || null });
          setBusy(false);
          if (r.error) return setError(errorText(t, r.error));
          announce(decide.approve ? t['pa.approved'] : t['pa.rejected']);
          setDecide(null);
          setNote('');
          await load();
          onChanged?.();
        }}
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-n`}>
            {t['pa.noteOptional']}
          </label>
          <input id={`${id}-n`} className="field" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </Dialog>
    </section>
  );
}

function daysLeft(expires: string): number {
  return Math.ceil((new Date(expires).getTime() - Date.now()) / 86400000);
}

function roleWithScope(t: Strings, role: RoleName, opts: { primary?: boolean; scope?: string[] | null }): string {
  const name = t[`pa.roleName_${role}`];
  if (role === 'head_of_school' && opts.primary) return `${name} (${t['hs.primaryMark']})`;
  if (role === 'counsellor' && opts.scope !== undefined) {
    return `${name} (${opts.scope && opts.scope.length > 0 ? t['hs.gradesList'](opts.scope.join(', ')) : t['hs.wholeSchool']})`;
  }
  return name;
}

function beforeText(t: Strings, r: Request): string {
  if (r.subject_roles.length === 0) return t['hs.noRolesYet'];
  return r.subject_roles.map((x) => roleWithScope(t, x.role, { primary: x.is_primary })).join(', ');
}

function afterText(t: Strings, r: Request): string {
  const now = r.subject_roles.map((x) => roleWithScope(t, x.role, { primary: x.is_primary }));
  return [...now, roleWithScope(t, r.role, { primary: r.make_primary, scope: r.scope_grades })].join(', ');
}
